import { trackLead } from "@/lib/metaPixel";
import { trackSignUp, getCampaign } from "@/lib/gtm";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth, dashboardFor } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Compass, Mail, Lock, User as UserIcon, Users, Store, CalendarPlus, ShoppingBag, Sparkles, CheckCircle2, ArrowLeft, Check, X as XIcon } from "lucide-react";
import { evaluatePasswordStrength } from "@/lib/passwordStrength";
import { BUNDLE_COMPLETED_KEY } from "@/pages/BundleOnboarding";

// Bump these when the Terms or Privacy copy changes materially — the string is
// stored on the user doc at registration so admins have an audit trail.
export const TERMS_VERSION = "2026-02-01";
export const PRIVACY_VERSION = "2026-02-01";

// Compose a full display name from the split first/surname inputs. Keeping
// `name` consistent means older code paths (emails, backend fallbacks) still
// work transparently.
const fullName = (f) => `${(f.first_name || "").trim()} ${(f.surname || "").trim()}`.trim();

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { setUser, paymentsEnabled } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ first_name: "", surname: "", name: "", email: "", password: "", role: "parent" });
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(null);
  // Required consents on registration — both must be ticked before Sign Up is enabled.
  // Unticked by default on every fresh render of the register page.
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  // Multi-role login state
  const [roleOptions, setRoleOptions] = useState(null); // array of {role,label} if multiple
  const [selectedRole, setSelectedRole] = useState("");
  // Resume-bundle-onboarding banner shown on both /login and /register when a
  // previous bundle sign-up session was saved to localStorage.
  const [bundlePending, setBundlePending] = useState(null);
  // Inline Terms / Privacy preview — avoids navigating away from the form on
  // mobile, which previously lost the sign-up state when the link opened a new
  // tab. Also used by the "Tick both boxes above" hint to scroll the consent
  // block into view when the Sign Up button is disabled.
  const [legalOpen, setLegalOpen] = useState(null); // "terms" | "privacy" | null
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const consentRef = useRef(null);
  const focusConsent = () => {
    consentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    // Flash the consent block briefly so mobile users notice it.
    const el = consentRef.current;
    if (el) {
      el.classList.add("ring-4", "ring-terracotta/50");
      setTimeout(() => el.classList.remove("ring-4", "ring-terracotta/50"), 1200);
    }
  };


  useEffect(() => {
    let email = null;
    try { email = JSON.parse(localStorage.getItem(BUNDLE_COMPLETED_KEY) || "null")?.email; } catch { /* ignore */ }
    if (!email) return;
    api.post("/auth/bundle-approval-status", { email })
      .then(({ data }) => {
        if (data.bundle && data.pending.length > 0) {
          setBundlePending(data.pending);
        } else if (data.bundle) {
          localStorage.removeItem(BUNDLE_COMPLETED_KEY);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isLogin) {
      const prefill = new URLSearchParams(location.search).get("email");
      if (prefill) setForm((f) => ({ ...f, email: prefill }));
    }
  }, [isLogin, location.search]);

  const googleLogin = () => {
    // When signing up (not logging in) and a specific role is picked, stash it
    // so AuthCallback can tell the backend which role the user is joining as.
    // This drives: (a) sign-in to that role if it exists under this email,
    // (b) add that role + run the profile-setup flow if the email exists but
    // doesn't yet have the role, (c) fresh registration if the email is new.
    try {
      if (!isLogin && form.role && form.role !== "bundle") {
        sessionStorage.setItem("cck_google_desired_role_v1", form.role);
      } else {
        sessionStorage.removeItem("cck_google_desired_role_v1");
      }
    } catch { /* storage disabled */ }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/home";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const finishLogin = (data) => {
    setUser(data);
    toast.success(`Welcome back, ${data.name}!`);
    const nextParam = new URLSearchParams(location.search).get("next");
    const fromState = location.state?.from?.pathname;
    const preferred = nextParam || fromState;
    navigate(preferred && preferred !== "/login" ? preferred : dashboardFor(data));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin && form.role === "bundle" && paymentsEnabled === false) {
      setLoading(true);
      try {
        await api.post("/auth/bundle-start", {
          email: form.email, name: fullName(form), first_name: form.first_name.trim(), surname: form.surname.trim(),
          password: form.password, terms_version: TERMS_VERSION, privacy_version: PRIVACY_VERSION,
        });
        trackLead({ content_name: "bundle" });
        setRegistered(true);
      } catch (err) {
        toast.error(errMsg(err, "Registration failed"));
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!isLogin && form.role === "bundle") {
      try {
        sessionStorage.setItem("cck_bundle_creds_v1", JSON.stringify({
          email: form.email, name: fullName(form), first_name: form.first_name, surname: form.surname,
          password: form.password,
          terms_version: TERMS_VERSION, privacy_version: PRIVACY_VERSION,
        }));
      } catch { /* storage disabled */ }
      navigate("/checkout?plan=bundle", { state: { email: form.email, name: fullName(form) } });
      return;
    }
    setLoading(true);
    try {
      if (isLogin) {
        const payload = { email: form.email, password: form.password };
        const { data } = await api.post("/auth/login", payload);
        if (data && data.multiple_roles) {
          setRoleOptions(data.roles || []);
          setSelectedRole((data.roles && data.roles[0]?.role) || "");
          toast.info("This email has more than one profile — pick which one to sign into.");
          return;
        }
        finishLogin(data);
      } else {
        await api.post("/auth/register", {
          ...form,
          name: fullName(form),
          first_name: form.first_name.trim(),
          surname: form.surname.trim(),
          terms_version: TERMS_VERSION,
          privacy_version: PRIVACY_VERSION,
          campaign: getCampaign(),
        });
        trackLead({ content_name: form.role || "unknown" });
        trackSignUp(form.role, "email");
        setRegistered(true);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (!isLogin && detail && typeof detail === "object" && detail.code === "role_already_registered") {
        setAlreadyRegistered({
          role: detail.role,
          roleLabel: detail.role_label,
          email: detail.email || form.email,
        });
        setLoading(false);
        return;
      }
      const msg = errMsg(err, isLogin ? "Sign in failed" : "Registration failed");
      if (msg === "email_not_verified") {
        setNeedsVerify(true);
        toast.error("Please confirm your email before signing in.");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const submitRoleChoice = async (e) => {
    e.preventDefault();
    if (!selectedRole) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: form.email, password: form.password, role: selectedRole });
      finishLogin(data);
    } catch (err) {
      toast.error(errMsg(err, "Sign in failed"));
    } finally {
      setLoading(false);
    }
  };

  const cancelRoleChoice = () => {
    setRoleOptions(null);
    setSelectedRole("");
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification", { email: form.email, password: form.password || "x" });
      toast.success("If your account exists, a new confirmation link is on its way.");
    } catch {
      toast.error("Could not resend right now.");
    }
  };

  if (registered) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 grain" data-testid="register-success">
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 sm:p-10 max-w-md w-full text-center shadow-sm">
          <span className="inline-flex w-16 h-16 rounded-full bg-sage-light items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-sage-dark" />
          </span>
          <h1 className="text-2xl font-black text-forest mb-2">Check your inbox</h1>
          <p className="text-forest/70 text-sm leading-relaxed mb-6">
            We've sent a confirmation link to <strong>{form.email}</strong>. Click it to complete your registration, then sign in.
          </p>
        </div>
      </div>
    );
  }

  if (!isLogin && alreadyRegistered) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid="role-already-registered">
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 sm:p-10 max-w-md w-full text-center shadow-sm">
          <span className="inline-flex w-16 h-16 rounded-full bg-sage-light items-center justify-center mb-4">
            <CheckCircle2 className="w-8 h-8 text-sage-dark" />
          </span>
          <h1 className="text-2xl font-black text-forest mb-2" data-testid="already-registered-title">
            {alreadyRegistered.roleLabel} account already exists
          </h1>
          <p className="text-forest/70 text-sm leading-relaxed mb-6" data-testid="already-registered-message">
            <strong>{alreadyRegistered.email}</strong> is already registered as {alreadyRegistered.roleLabel[0].match(/[AEIOU]/i) ? "an" : "a"}{" "}
            <strong>{alreadyRegistered.roleLabel}</strong>. Sign in and pick the {alreadyRegistered.roleLabel} role from your role picker.
          </p>
          <Link to={`/login?email=${encodeURIComponent(alreadyRegistered.email)}`} state={{ prefillEmail: alreadyRegistered.email }}>
            <Button className="rounded-full bg-sage hover:bg-sage-dark text-white w-full mb-3" data-testid="already-registered-signin-btn">
              Sign in to your {alreadyRegistered.roleLabel} account
            </Button>
          </Link>
          <Button
            type="button"
            variant="outline"
            onClick={() => setAlreadyRegistered(null)}
            className="w-full rounded-full border-sage/40 text-forest hover:bg-sage-light hover:text-forest"
            data-testid="already-registered-try-different-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Register a different role or email
          </Button>
        </div>
      </div>
    );
  }

  // Multi-role selection screen (only reached on login flow)
  if (isLogin && roleOptions) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid="login-role-picker">
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 sm:p-10 max-w-md w-full shadow-sm fade-in">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-10 h-10 rounded-full bg-sage flex items-center justify-center">
              <Compass className="w-5 h-5 text-white" />
            </span>
            <div>
              <h1 className="text-xl font-black text-forest leading-tight">Choose a role to sign in as</h1>
              <p className="text-xs text-forest/60">{form.email}</p>
            </div>
          </div>
          <p className="text-sm text-forest/70 mb-4">This email is registered for more than one profile. Pick which one you want to open — you can sign out and switch anytime.</p>
          <form onSubmit={submitRoleChoice} className="space-y-3">
            {roleOptions.map((r) => (
              <label
                key={r.role}
                className={`block rounded-2xl border-2 p-4 cursor-pointer transition-colors ${selectedRole === r.role ? "border-sage bg-sage-50" : "border-sage/20 bg-white hover:border-sage/50"}`}
                data-testid={`login-role-option-${r.role}`}
              >
                <input
                  type="radio"
                  name="loginRole"
                  value={r.role}
                  checked={selectedRole === r.role}
                  onChange={() => setSelectedRole(r.role)}
                  className="sr-only"
                  data-testid={`login-role-radio-${r.role}`}
                />
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-heading font-bold text-forest">{r.label}</div>
                    <div className="text-xs text-forest/60">Sign in to your {r.label} dashboard</div>
                  </div>
                  {selectedRole === r.role && <CheckCircle2 className="w-5 h-5 text-sage-dark" />}
                </div>
              </label>
            ))}
            <Button type="submit" disabled={loading || !selectedRole} className="w-full rounded-full bg-sage hover:bg-sage-dark text-white shadow-md" data-testid="login-role-continue-btn">
              {loading ? "Signing you in..." : "Continue"}
            </Button>
            <Button type="button" variant="ghost" onClick={cancelRoleChoice} className="w-full rounded-full text-forest/70" data-testid="login-role-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Use a different email
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid={isLogin ? "login-page" : "register-page"}>
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 sm:p-10 max-w-md w-full shadow-sm fade-in">
        <div className="flex items-center gap-2 mb-6">
          <span className="w-10 h-10 rounded-full bg-sage flex items-center justify-center">
            <Compass className="w-5 h-5 text-white" />
          </span>
          <div>
            <h1 className="text-xl font-black text-forest leading-tight">{isLogin ? "Welcome back" : "Sign Up"}</h1>
            <p className="text-xs text-forest/60">{isLogin ? "Sign in to your account" : "Join Compass Kids — choose your role"}</p>
          </div>
        </div>

        {bundlePending && (
          <div className="w-full mb-5 rounded-2xl border-2 border-terracotta/40 bg-terracotta-light/40 p-3" data-testid="bundle-pending-banner">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-terracotta-dark shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-black uppercase tracking-wide text-terracotta-dark">Bundle profiles awaiting approval</p>
                <ul className="mt-1 space-y-0.5">
                  {bundlePending.map((r) => (
                    <li key={r.role} className="text-sm font-bold text-forest" data-testid={`bundle-pending-role-${r.role}`}>
                      {r.label} — {r.status === "rejected" ? "not approved" : "pending admin approval"}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-forest/70 mt-0.5">Your Parent profile is live. These go live once approved.</p>
              </div>
            </div>
          </div>
        )}


        {!isLogin && (
          <div className="grid grid-cols-2 gap-3 mb-6" data-testid="role-picker">
            {[
              { value: "parent", label: "Parent", icon: Users, desc: "Find events & vendors" },
              { value: "vendor", label: "Vendor", icon: Store, desc: "Offer services" },
              { value: "event_creator", label: "Event Creator", icon: CalendarPlus, desc: "Host events" },
              { value: "marketplace_user", label: "Marketplace User", icon: ShoppingBag, desc: "Sell products" },
            ].map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setForm({ ...form, role: r.value })}
                className={`rounded-2xl border-2 p-4 text-left transition-colors ${
                  form.role === r.value ? "border-sage bg-sage-50" : "border-sage/20 bg-white hover:border-sage/50"
                }`}
                data-testid={`role-${r.value}-btn`}
              >
                <r.icon className={`w-5 h-5 mb-2 ${form.role === r.value ? "text-sage-dark" : "text-forest/50"}`} />
                <div className="font-heading font-bold text-sm text-forest flex items-center gap-1">
                  {r.label}
                  {form.role === r.value && <CheckCircle2 className="w-4 h-4 text-sage-dark" />}
                </div>
                <div className="text-xs text-forest/60">{r.desc}</div>
                <div className="mt-1 text-[11px] leading-snug text-stone-500" data-testid={`role-${r.value}-note`}>
                  {r.value === "parent"
                    ? "Free Forever — no credit card required."
                    : "Free Early Access until 30 November 2026, then normal pricing applies. Have a discount code? Enter it at checkout."}
                </div>
                {r.value === "parent" && (
                  <div className="mt-2 space-y-0.5" data-testid="role-parent-pricing">
                    <div className="text-[11px] font-bold text-terracotta-dark" data-testid="role-parent-standard-price">Free — no payment required</div>
                  </div>
                )}
                {(r.value === "vendor" || r.value === "event_creator" || r.value === "marketplace_user") && (
                  <div className="mt-2 space-y-0.5" data-testid={`role-${r.value}-pricing`}>
                    <div className="text-[11px] font-bold text-terracotta-dark" data-testid={`role-${r.value}-standard-price`}>R149/month Standard Price</div>
                  </div>
                )}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setForm({ ...form, role: "bundle" })}
              className={`col-span-2 rounded-2xl border-2 p-4 text-left transition-colors relative overflow-hidden ${
                form.role === "bundle"
                  ? "border-terracotta bg-terracotta-light/40"
                  : "border-terracotta/30 bg-cream hover:border-terracotta/60"
              }`}
              data-testid="role-bundle-btn"
            >
              <span className="absolute top-2 right-3 text-[10px] font-black uppercase tracking-wider text-terracotta-dark bg-white/80 px-2 py-0.5 rounded-full">
                Best value
              </span>
              <div className="flex items-start gap-3">
                <Sparkles className={`w-6 h-6 mt-0.5 shrink-0 ${form.role === "bundle" ? "text-terracotta-dark" : "text-terracotta"}`} />
                <div>
                  <div className="font-heading font-bold text-sm text-forest flex items-center gap-1.5">
                    User Bundle Deal — All 4 Roles
                    {form.role === "bundle" && <CheckCircle2 className="w-4 h-4 text-terracotta-dark" />}
                  </div>
                  <div className="text-xs text-forest/70 mt-0.5">
                    Parent, Vendor, Event Creator and Marketplace User — unlocked under one sign-up.
                  </div>
                  <div className="mt-1 text-[11px] leading-snug text-stone-500" data-testid="role-bundle-note">
                    Free Early Access until 30 November 2026, then normal pricing applies. Have a discount code? Enter it at checkout.
                  </div>
                  <div className="mt-2 space-y-0.5" data-testid="role-bundle-pricing">
                    <div className="text-[11px] font-bold text-terracotta-dark" data-testid="role-bundle-standard-price">R199/month Standard Price</div>
                    <div className="text-[11px] font-black text-forest" data-testid="role-bundle-saving">Saving R347</div>
                  </div>
                </div>
              </div>
            </button>
          </div>
        )}

        {!isLogin && (
          <div className="rounded-xl bg-sage-50 border border-sage/20 p-3 text-xs text-forest/70 mb-4" data-testid="multi-role-hint">
            Already have a Compass Kids account with this email? You can add another role — each role has its own password, verification, admin approval and payment, so this sign-up is fully independent.
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          {!isLogin && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="first-name" className="text-forest/80">First name</Label>
                <div className="relative mt-1">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
                  <Input id="first-name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className="pl-9 rounded-xl bg-softstone/60 border-sage/30" placeholder="Jane" data-testid="first-name-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="surname" className="text-forest/80">Surname</Label>
                <Input id="surname" required value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })}
                  className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="Doe" data-testid="surname-input" />
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="email" className="text-forest/80">Email</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="pl-9 rounded-xl bg-softstone/60 border-sage/30" placeholder="you@example.com" data-testid="email-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="password" className="text-forest/80">Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="pl-9 rounded-xl bg-softstone/60 border-sage/30" placeholder="At least 6 characters" data-testid="password-input" />
            </div>
            {isLogin && (
              <div className="mt-1.5 text-right">
                <button
                  type="button"
                  onClick={() => { setForgotEmail(form.email || ""); setForgotOpen(true); }}
                  className="text-xs font-bold text-terracotta-dark hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            )}
            {!isLogin && form.password && (() => {
              const s = evaluatePasswordStrength(form.password);
              return (
                <div className="mt-1.5" data-testid="password-strength">
                  <div className="h-1.5 w-full bg-sage-light/40 rounded-full overflow-hidden">
                    <div className={`h-full ${s.color} transition-all`} style={{ width: `${s.score}%` }} data-testid="password-strength-bar" />
                  </div>
                  <p className="text-[11px] text-forest/60 mt-1" data-testid="password-strength-label">Strength: <span className="font-bold">{s.label}</span></p>
                </div>
              );
            })()}
          </div>

          {!isLogin && (
            <div>
              <Label htmlFor="confirm-password" className="text-forest/80">Confirm password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
                <Input id="confirm-password" type="password" required minLength={6} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-9 rounded-xl bg-softstone/60 border-sage/30" placeholder="Re-type your password" data-testid="confirm-password-input" />
              </div>
              {confirmPassword && (
                form.password === confirmPassword ? (
                  <p className="text-[11px] text-sage-dark mt-1 flex items-center gap-1" data-testid="confirm-password-match">
                    <Check className="w-3 h-3" /> Passwords match
                  </p>
                ) : (
                  <p className="text-[11px] text-terracotta-dark mt-1 flex items-center gap-1" data-testid="confirm-password-mismatch">
                    <XIcon className="w-3 h-3" /> Passwords do not match
                  </p>
                )
              )}
            </div>
          )}

          {needsVerify && (
            <div className="rounded-xl bg-terracotta-light border border-terracotta/30 p-3 text-xs text-forest/80" data-testid="verify-notice">
              Your email isn't confirmed yet.{" "}
              <button type="button" onClick={resend} className="font-bold text-terracotta-dark underline" data-testid="resend-verify-btn">
                Resend confirmation email
              </button>
            </div>
          )}

          {!isLogin && (
            <div ref={consentRef} className="space-y-3 rounded-xl bg-terracotta-light/60 border-2 border-terracotta/40 p-4 transition-shadow" data-testid="consent-block">
              <p className="text-xs font-bold text-terracotta-dark uppercase tracking-wide" data-testid="consent-header">
                Required — please tick both to continue
              </p>
              <label className="flex items-start gap-3 text-sm text-forest cursor-pointer select-none" data-testid="agree-terms-label">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="mt-0.5 w-5 h-5 shrink-0 accent-sage-dark cursor-pointer border-2 border-forest/40 rounded"
                  data-testid="agree-terms-checkbox"
                />
                <span>
                  I agree to the{" "}
                  <button
                    type="button"
                    onClick={() => setLegalOpen("terms")}
                    className="font-bold text-terracotta-dark underline"
                    data-testid="terms-link"
                  >
                    Terms &amp; Conditions
                  </button>
                  .
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm text-forest cursor-pointer select-none" data-testid="agree-privacy-label">
                <input
                  type="checkbox"
                  checked={agreePrivacy}
                  onChange={(e) => setAgreePrivacy(e.target.checked)}
                  className="mt-0.5 w-5 h-5 shrink-0 accent-sage-dark cursor-pointer border-2 border-forest/40 rounded"
                  data-testid="agree-privacy-checkbox"
                />
                <span>
                  I agree to the{" "}
                  <button
                    type="button"
                    onClick={() => setLegalOpen("privacy")}
                    className="font-bold text-terracotta-dark underline"
                    data-testid="privacy-link"
                  >
                    Privacy Policy
                  </button>
                  .
                </span>
              </label>
            </div>
          )}

          <Button type="submit" disabled={loading || (!isLogin && (!agreeTerms || !agreePrivacy || !confirmPassword || form.password !== confirmPassword))} className="w-full rounded-full bg-sage hover:bg-sage-dark text-white shadow-md" data-testid="auth-submit-btn">
            {loading ? "Please wait..." : isLogin ? "Sign in" : "Sign Up"}
          </Button>
          {!isLogin && (!agreeTerms || !agreePrivacy) && (
            <button
              type="button"
              onClick={focusConsent}
              className="w-full text-[11px] text-center text-terracotta-dark underline-offset-2 hover:underline"
              data-testid="consent-hint"
            >
              Tick both boxes above to enable Sign Up.
            </button>
          )}
        </form>

        {!isLogin && (
          <Dialog open={legalOpen !== null} onOpenChange={(open) => !open && setLegalOpen(null)}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`legal-dialog-${legalOpen || "closed"}`}>
              <DialogHeader>
                <DialogTitle data-testid="legal-dialog-title">
                  {legalOpen === "terms" ? "Terms & Conditions" : "Privacy Policy"}
                </DialogTitle>
                <DialogDescription>
                  {legalOpen === "terms"
                    ? `Version ${TERMS_VERSION} · Please read before accepting.`
                    : `Version ${PRIVACY_VERSION} · Please read before accepting.`}
                </DialogDescription>
              </DialogHeader>
              {legalOpen === "terms" ? (
                <div className="space-y-3 text-sm text-forest/80 leading-relaxed" data-testid="legal-dialog-terms-body">
                  <p>Welcome to Compass Kids. By creating an account you agree to use the platform respectfully, keep your login details private, and share accurate information about yourself and (where applicable) your business.</p>
                  <h3 className="font-black text-forest">Your account</h3>
                  <p>Each role (Parent, Vendor, Event Creator, Marketplace) is a separate account under your email, with its own verification, admin approval and (where applicable) subscription. You remain responsible for the activity that happens on each of your accounts.</p>
                  <h3 className="font-black text-forest">Community conduct</h3>
                  <p>Treat other families, hosts, vendors and admins with kindness. Harassment, discrimination, spam, or any content that puts children at risk will result in immediate removal from the platform. Admins may approve, decline, edit, or remove any listing, event, meet-up or message at their discretion.</p>
                  <h3 className="font-black text-forest">Payments &amp; subscriptions</h3>
                  <p>Vendor, Event Creator and Marketplace memberships are billed monthly via Paystack. You can cancel from your portal at any time; your access continues until the end of the current period. Launch pricing is honoured until a plan change is announced on the platform.</p>
                  <h3 className="font-black text-forest">Changes</h3>
                  <p>We may update these terms as the platform evolves. Material changes will be communicated by email and on your dashboard before they take effect.</p>
                </div>
              ) : (
                <div className="space-y-3 text-sm text-forest/80 leading-relaxed" data-testid="legal-dialog-privacy-body">
                  <p>Compass Kids collects only what we need to run a safe, family-friendly community platform. We never sell your personal information.</p>
                  <h3 className="font-black text-forest">What we collect</h3>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Account details (name, email, hashed password or Google sign-in).</li>
                    <li>Role-specific profile data (business details for Vendors / Marketplace Users / Event Creators; children's ages &amp; genders for Parents who choose to share them).</li>
                    <li>RSVP, meet-up, chat and announcement activity you create.</li>
                    <li>Payment metadata from Paystack (we never store your card number — Paystack holds that).</li>
                    <li>Approximate location (if you enter a suburb) to show nearby events and meet-ups.</li>
                  </ul>
                  <h3 className="font-black text-forest">How we use it</h3>
                  <p>To run your profile, send approval and payment notifications, show you relevant local activity, and let admins moderate the community. Marketing emails are opt-in only.</p>
                  <h3 className="font-black text-forest">Children's data</h3>
                  <p>Children's names are never shown to other users. Only ages and (optionally) gender are visible on attendee lists, and only to confirmed meet-up participants. You can edit or delete this at any time from your profile.</p>
                  <h3 className="font-black text-forest">Your rights</h3>
                  <p>You can download, correct or permanently delete your account data from your profile page at any time. Reach us at <a href="mailto:info@compasskids.co.za" className="font-bold text-terracotta-dark hover:underline">info@compasskids.co.za</a> for anything else.</p>
                </div>
              )}
              <DialogFooter>
                <DialogClose asChild>
                  <Button
                    type="button"
                    onClick={() => {
                      if (legalOpen === "terms") setAgreeTerms(true);
                      else if (legalOpen === "privacy") setAgreePrivacy(true);
                      setLegalOpen(null);
                    }}
                    className="w-full rounded-full bg-sage hover:bg-sage-dark text-white"
                    data-testid="legal-dialog-accept-btn"
                  >
                    I understand &amp; accept
                  </Button>
                </DialogClose>
                <DialogClose asChild>
                  <Button type="button" variant="outline" className="w-full rounded-full" data-testid="legal-dialog-close-btn">
                    Close
                  </Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-sage/20" />
          <span className="text-xs text-forest/50">or</span>
          <div className="flex-1 h-px bg-sage/20" />
        </div>

        <Button type="button" variant="outline" onClick={googleLogin} className="w-full rounded-full border-sage/40 text-forest hover:bg-sage-light hover:text-forest" data-testid="google-login-btn">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a7.2 7.2 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
          Continue with Google
        </Button>

        <p className="text-center text-sm text-forest/60 mt-6">
          {isLogin ? (
            <>New here? <Link to="/register" className="font-bold text-terracotta-dark hover:underline" data-testid="goto-register-link">Sign Up</Link></>
          ) : (
            <>Already a member? <Link to="/login" className="font-bold text-terracotta-dark hover:underline" data-testid="goto-login-link">Sign in</Link></>
          )}
        </p>

        <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
          <DialogContent className="rounded-3xl bg-cream" data-testid="forgot-password-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-forest">Reset your password</DialogTitle>
              <DialogDescription className="text-sm text-forest/70">
                Enter the email address on your account and we'll send you a link to set a new password.
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const email = forgotEmail.trim();
                if (!email) { toast.error("Please enter your email address."); return; }
                setForgotSending(true);
                try {
                  await api.post("/auth/forgot-password", { email });
                  toast.success("If that email is registered, a reset link is on its way.");
                  setForgotOpen(false);
                } catch (err) {
                  toast.error(errMsg(err, "Could not send reset email"));
                } finally {
                  setForgotSending(false);
                }
              }}
              className="space-y-4"
            >
              <div>
                <Label htmlFor="forgot-email" className="text-forest/80">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
                  <Input
                    id="forgot-email"
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="pl-9 rounded-xl bg-softstone/60 border-sage/30"
                    placeholder="you@example.com"
                    data-testid="forgot-password-email-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={forgotSending}
                  className="w-full rounded-full bg-sage hover:bg-sage-dark text-white"
                  data-testid="forgot-password-submit-btn"
                >
                  {forgotSending ? "Sending..." : "Send reset link"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
